/**
 * Modal "Ingesta de JSON" — para pruebas del pickeo.
 *
 * Se pega el detalle de un comprobante (mismo JSON que devuelve ACSIS para
 * `/obtener/<recurso>/<referencia>.json`) y el back crea una tarea de prueba
 * con esos datos, sin consultar a Plataforma. Sirve para armar remitos con
 * los lotes y SKU que hay físicamente para pickear.
 *
 * La referencia sale del JSON (`division-sucursal-tipo-numero`) y no puede
 * repetirse: para ingerir otra vez el mismo remito, cambiar el `numero`.
 * La tarea no existe en Plataforma, así que el cierre en el ERP después de
 * trazar va a fallar (queda como error, no rompe nada).
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/notify';
import { useIngestaManualMutation } from '@/api/queries';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  glnId: string | null;
}

/** Parsea el texto pegado. Devuelve el objeto o el motivo por el que no es JSON válido. */
function parsear(texto: string): { ok: true; valor: unknown } | { ok: false; error: string } {
  if (!texto.trim()) return { ok: false, error: 'Pegá el JSON del comprobante.' };
  try {
    return { ok: true, valor: JSON.parse(texto) as unknown };
  } catch (err) {
    return { ok: false, error: `No es un JSON válido: ${err instanceof Error ? err.message : ''}` };
  }
}

export function IngestaJsonDialog({ open, onOpenChange, glnId }: Props) {
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mutation = useIngestaManualMutation();

  const cerrar = (o: boolean) => {
    if (mutation.isPending) return;
    if (!o) setError(null);
    onOpenChange(o);
  };

  const handleIngestar = () => {
    if (!glnId) {
      setError('No hay GLN activo. Elegí un GLN antes de hacer la ingesta.');
      return;
    }
    const parsed = parsear(texto);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setError(null);
    mutation.mutate(
      { glnId, detalle: parsed.valor },
      {
        onSuccess: (r) => {
          toast.success(
            `Ingesta OK · ${r.referencia} · ${r.partidas} partida${r.partidas === 1 ? '' : 's'}`,
          );
          setTexto('');
          onOpenChange(false);
        },
        onError: (err) => setError(err.message),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Ingesta de JSON</DialogTitle>
          <DialogDescription className="text-xs">
            Pegá el detalle de un comprobante con el mismo formato que devuelve Plataforma. Se crea
            una tarea de prueba en el GLN activo, sin consultar al ERP. La referencia sale de
            division-sucursal-tipo-numero y no puede repetirse.
          </DialogDescription>
        </DialogHeader>

        <textarea
          value={texto}
          onChange={(e) => {
            setTexto(e.target.value);
            if (error) setError(null);
          }}
          spellCheck={false}
          placeholder={'{\n  "response": {\n    "division": "1",\n    ...\n  }\n}'}
          className="h-96 w-full resize-y rounded-md border border-gray-200 p-3 font-mono text-xs outline-none focus:border-everex-naranja"
          aria-label="JSON del comprobante"
          disabled={mutation.isPending}
        />

        {error && <p className="text-xs text-red-700">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => cerrar(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={handleIngestar}
            disabled={mutation.isPending || !texto.trim()}
            className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
          >
            {mutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Ingestar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
